// =============================================
// ContentEnginePanel — Real content production center
// Generate, monitor, manage articles for all nodes in a map
// =============================================

import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  FileText, Play, Zap, Search, Filter, AlertCircle, CheckCircle2,
  Clock, Loader2, RotateCcw, Terminal, X,
  ChevronDown, ChevronRight, ListTree, Layers, Trash2, Eye, Download,
  Unlock, Lock,
} from "lucide-react";
import { downloadLogRunAsTxt, isLogCollectorActive, listLogRuns, clearAllLogRuns } from "@/services/contentEngine/contentLogger";
import { EngineProgress } from "./EngineProgress";
import { SchemaReadinessBadge } from "./SchemaReadinessBadge";
import { ArticlePreviewDialog } from "./ArticlePreviewDialog";
import { useThematicNodes } from "@/hooks/useThematicNodes";
import { useThematicSeeds } from "@/hooks/useThematicSeeds";
import { useThematicNodeContent } from "@/hooks/useThematicNodeContent";
import { useWorkerConfig } from "@/hooks/useWorkerConfig";
import { LiveIndicator } from "@/components/ui/LiveIndicator";
import { useContentEngine } from "@/hooks/useContentEngine";
import { useToast } from "@/hooks/use-toast";
import { generateArticle, generateStructure, generateContent } from "@/services/contentEngine";
import { tryAcquire, release } from "@/services/llm/runLock";
import { getBrokenLinksScores } from "@/services/contentEngine/brokenLinksSort";
import { resetNodeSkipStatus } from "@/services/contentEngine/resetSkipStatus";
import { supabase } from "@/integrations/supabase/client";
import { isWorkerModeEnabled } from "@/lib/workerMode";
import { env } from "@/lib/env";
import { enqueueGenerationJob, type GenerationStages } from "@/services/queueClient";
import { ConfirmRegenerateDialog, type RegenerateTarget } from "./ConfirmRegenerateDialog";
import { StageDistributionHeatmap } from "./StageDistributionHeatmap";
import { WidgetErrorBoundary } from "@/components/WidgetErrorBoundary";
import type { EngineStage } from "@/types/thematicMaps";

const TERMINAL_STAGES = new Set<string>(["ready", "published", "skipped_existing"]);

interface ContentEnginePanelProps {
  mapId: string;
}

const LIFECYCLE_LABELS: Record<string, { label: string; color: string }> = {
  not_started: { label: "Brak treści", color: "bg-muted text-muted-foreground" },
  context_ready: { label: "Kontekst gotowy", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
  outline_ready: { label: "Struktura gotowa", color: "bg-violet-500/15 text-violet-700 dark:text-violet-400" },
  in_generation: { label: "Generowanie...", color: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  ready: { label: "Gotowy (QA pending)", color: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400" },
  needs_review: { label: "Wymaga recenzji", color: "bg-orange-500/15 text-orange-700 dark:text-orange-400" },
  approved: { label: "Zatwierdzony", color: "bg-green-500/15 text-green-700 dark:text-green-400" },
  scheduled: { label: "Zaplanowany", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
  published: { label: "Opublikowany", color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  skipped_existing: { label: "Pominięte (istniejący wpis)", color: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-400" },
};

const IN_GENERATION_STAGES = new Set(["serp_research", "kg_assembly", "fan_out", "outline", "draft", "enrichment", "optimization", "humanization"]);

function computeLifecycle(content: any): string {
  const stage = content?.engine_stage || "pending";
  const qa = (content?.qa_status ?? "pending");

  if (stage === "skipped_existing") return "skipped_existing";
  // Prawda o publikacji na WP siedzi w wp_post_id/published_at — publikacja
  // nie zawsze przestawia engine_stage, wiec opublikowane artykuly
  // pokazywaly "Zatwierdzony" zamiast "Opublikowany" (fix 2026-08-24)
  if (content?.published_at || content?.wp_post_id) return "published";
  if (stage === "published") return "published";
  if (IN_GENERATION_STAGES.has(stage)) return "in_generation";
  if (stage === "outline_ready") return "outline_ready";
  if (stage === "ready" && qa === "approved") return "approved";
  if (stage === "ready" && (qa === "needs_review" || qa === "failed")) return "needs_review";
  if (stage === "ready") return "ready";
  if (content?.current_context_hash && !content?.final_html) return "context_ready";
  return "not_started";
}

function canPublish(content: any): { allowed: boolean; reason?: string } {
  const qa = (content?.qa_status ?? "pending");
  const isStale = (content?.is_stale ?? false);
  const needsRegen = (content?.needs_regeneration ?? false);

  if (qa !== "approved") return { allowed: false, reason: `QA: ${qa}` };
  if (isStale === true) return { allowed: false, reason: "Nieaktualne" };
  if (needsRegen === true) return { allowed: false, reason: "Wymaga regeneracji" };
  if (!content?.final_html) return { allowed: false, reason: "Brak treści" };
  if (content?.engine_stage !== "ready") return { allowed: false, reason: `Stage: ${content?.engine_stage}` };
  if (content?.published_at || content?.wp_post_id) return { allowed: false, reason: "Już opublikowany" };
  return { allowed: true };
}

export function ContentEnginePanel({ mapId }: ContentEnginePanelProps) {
  const { nodes } = useThematicNodes(mapId);
  const { seeds, updateSeed, refreshSeeds } = useThematicSeeds(mapId);
  const { contents, isLive: contentsLive } = useThematicNodeContent(mapId);
  const { useWorkerMode } = useWorkerConfig();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  // Limit dla "Pełny pipeline" — wpisz N żeby wziąć tylko pierwsze N od góry; puste = bez limitu
  const [batchLimitInput, setBatchLimitInput] = useState<string>("");
  const [generatingNodes, setGeneratingNodes] = useState<Set<string>>(new Set());
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState<Array<{ time: string; type: "info" | "success" | "error" | "stage"; message: string; debug?: Record<string, unknown> }>>([]);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [expandedOutlines, setExpandedOutlines] = useState<Set<string>>(new Set());
  const logsEndRef = useRef<HTMLDivElement>(null);
  const mainLogContainerRef = useRef<HTMLDivElement>(null);

  const [previewNodeId, setPreviewNodeId] = useState<string | null>(null);
  const [forceConfirm, setForceConfirm] = useState<RegenerateTarget | null>(null);

  // ============ Batch safeguards (JEDEN PRZYCISK — anti-zacinanie) ============
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchPaused, setBatchPaused] = useState(false);
  const [floatingMinimized, setFloatingMinimized] = useState(false);
  const [floatingDismissed, setFloatingDismissed] = useState(false);
  const [liveBatch, setLiveBatch] = useState<{
    current: number;
    total: number;
    activeNodes: Array<{ id: string; title: string; stage: string; startTs: number }>;
    etaMin: number;
    failed: number;    // result.success === false (LLM zwrócił błąd merytoryczny)
    skipped: number;   // hard timeout 35 min
    errored: number;   // exception / crash / network (systemowe)
    secsSinceLastActivity: number;
    concurrency: number;
  }>({ current: 0, total: 0, activeNodes: [], etaMin: 0, failed: 0, skipped: 0, errored: 0, secsSinceLastActivity: 0, concurrency: 2 });

  const floatingLogRef = useRef<HTMLDivElement>(null);

  const pauseRequestedRef = useRef<boolean>(false);
  const consecutiveFailsRef = useRef<number>(0);
  const lastProgressTsRef = useRef<number>(0);
  const batchStartTsRef = useRef<number>(0);
  const guardTriggeredRef = useRef<boolean>(false);
  const totalBatchRef = useRef<number>(0);
  const activeNodesRef = useRef<Map<string, { title: string; stage: string; startTs: number }>>(new Map());
  const heartbeatLogTsRef = useRef<number>(0);
  // Last 3 hard errors (timeout/crash/exception) — pokazujemy w spirala toast/log
  const recentErrorsRef = useRef<Array<{ title: string; reason: string; ts: number }>>([]);
  // Counter for consecutive HARD errors (timeout/exception) — NIE liczy result.success=false
  const consecutiveHardErrorsRef = useRef<number>(0);

  // User-selectable concurrency — ile node'ow rownolegle (1-5, default 2)
  const [batchConcurrencyInput, setBatchConcurrencyInput] = useState<string>("2");

  // Per-node STALE watchdog — jesli pipeline NIE WYSYLA zadnego progress callbacku
  // przez 18 min, znaczy ze zacial sie na JEDNYM etapie. Pojedynczy najdluzszy
  // stage = enrichment_verify (900s = 15min timeout per call). 18 min = 3 min margines.
  const STAGE_STALE_THRESHOLD_MS = 18 * 60 * 1000; // 18 min bez callbacku => stuck stage
  const STALE_CHECK_INTERVAL_MS = 30_000; // co 30s sprawdz czy stale
  // Hard cap na caly node — 30 min absolute max. Niezaleznie czy wysyla callbacki czy nie.
  // Po 30 min skip + lecimy do kolejnego.
  const NODE_HARD_TIMEOUT_MS = 30 * 60 * 1000; // 30 min absolute cap per node
  // Backup watchdog na poziomie batcha (jeszcze szersza siatka)
  const BATCH_STALE_MS = 32 * 60 * 1000; // 32 min globalna cisza batcha
  // Anti-spiral: liczone tylko errored + skipped (NIE failed result.success=false,
  // bo to czesto legit prompt-level problem konkretnego node, nie systemowe zacinanie)
  const BATCH_MAX_HARD_ERRORS = 3; // 3 crashe/stuck z rzedu => auto-pauza
  // Cooldown po hard error — daj LLM API oddechu, zmniejsz burst
  const POST_ERROR_COOLDOWN_MS = 5_000; // 5s
  const HEARTBEAT_LOG_INTERVAL_MS = 30 * 1000; // co 30s log heartbeat

  // Auto-scroll log container do dolu — scroll TYLKO container, NIE cala strone
  // (poprzednio scrollIntoView pociagal cala strone gdy log byl daleko od viewport)
  useEffect(() => {
    if (mainLogContainerRef.current) {
      mainLogContainerRef.current.scrollTop = mainLogContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Watchdog: 10 min stale => auto-pauza
  useEffect(() => {
    if (!batchRunning) return;
    const id = setInterval(() => {
      if (guardTriggeredRef.current || pauseRequestedRef.current) return;
      const silent = Date.now() - lastProgressTsRef.current;
      if (lastProgressTsRef.current > 0 && silent > BATCH_STALE_MS) {
        guardTriggeredRef.current = true;
        pauseRequestedRef.current = true;
        setBatchPaused(true);
        const min = Math.round(silent / 60_000);
        addLog("error", `⚠ Watchdog: brak progressu ${min} min (cap ${Math.round(BATCH_STALE_MS / 60_000)}min) => AUTO-PAUZA. Wznow recznie lub Stop.`);
        toast({
          title: "Watchdog: auto-pauza",
          description: `${min} min bez progressu. Klik 'Wznow' by kontynuowac.`,
          variant: "destructive",
          duration: 30_000,
        });
      }
    }, 15_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRunning]);

  // Tick interval: heartbeat + activeNodes refresh + secsSinceLastActivity (re-render co 1s)
  useEffect(() => {
    if (!batchRunning) return;
    const id = setInterval(() => {
      const secsSilent = lastProgressTsRef.current > 0
        ? Math.floor((Date.now() - lastProgressTsRef.current) / 1000)
        : 0;
      // Snapshot active nodes from ref
      const active = Array.from(activeNodesRef.current.entries()).map(([id, v]) => ({
        id,
        title: v.title,
        stage: v.stage,
        startTs: v.startTs,
      }));
      setLiveBatch((s) => ({
        ...s,
        activeNodes: active,
        secsSinceLastActivity: secsSilent,
      }));

      // Heartbeat log do dziennika co 30s — zeby user widzial ze system zyje
      // nawet jak pipeline robi dlugi LLM call bez intermediate callbackow
      if (active.length > 0 && Date.now() - heartbeatLogTsRef.current > HEARTBEAT_LOG_INTERVAL_MS) {
        heartbeatLogTsRef.current = Date.now();
        const stages = active.map((n) => `${n.title.slice(0, 30)}[${n.stage || "..."}]`).join(", ");
        addLog("info", `💓 Heartbeat — aktywne ${active.length} node(y): ${stages} | cisza ${secsSilent}s`);
      }
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchRunning]);

  // Auto-scroll floating window log do dolu
  useEffect(() => {
    if (floatingLogRef.current) {
      floatingLogRef.current.scrollTop = floatingLogRef.current.scrollHeight;
    }
  }, [logs]);

  // beforeunload warning
  useEffect(() => {
    if (!batchRunning) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      const msg = "Batch w trakcie — zamkniecie karty zatrzyma generowanie.";
      e.returnValue = msg;
      return msg;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [batchRunning]);

  const addLog = useCallback((type: "info" | "success" | "error" | "stage", message: string, debug?: Record<string, unknown>) => {
    const time = new Date().toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    setLogs((prev) => [...prev.slice(-200), { time, type, message, debug }]);
    if (!showLogs) setShowLogs(true);
  }, [showLogs]);

  const toggleLogExpand = (index: number) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  // Build node-to-content map — guards gdyby hook zwrocil undefined podczas loadingu
  const contentMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const c of (contents ?? [])) {
      if (c?.node_id) map.set(c.node_id, c);
    }
    return map;
  }, [contents]);

  // Build seed name map
  const seedNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of (seeds ?? [])) {
      if (s?.id) map.set(s.id, s.name ?? "");
    }
    return map;
  }, [seeds]);

  // Get engine stage for a node
  const getEngineStage = (nodeId: string): string => {
    const content = contentMap.get(nodeId);
    return content?.engine_stage || "pending";
  };

  // Tabela: sort widoku po kolumnie Seed (view-only, nie rusza batcha)
  const [tableSortBy, setTableSortBy] = useState<"default" | "seed_asc" | "seed_desc" | "broken_links" | "unfinished_first" | "qa_score_asc">("default");
  // Cycle dotyczy tylko Seed sort (broken_links + unfinished_first przez osobne dedicated buttony).
  const cycleTableSort = useCallback(() => {
    setTableSortBy((cur) =>
      cur === "default" ? "seed_asc" :
      cur === "seed_asc" ? "seed_desc" :
      "default",
    );
  }, []);

  // Priority sort "niewygenerowane na górze" — pending/failed/outline_ready najpierw, ready/published na końcu.
  // Niższy rank = wyżej w liście.
  const getUnfinishedRank = (stage: string): number => {
    if (stage === "pending") return 1;        // jeszcze nic nie zrobione
    if (stage === "failed") return 2;         // wymaga retry
    if (stage === "outline_ready") return 3;  // Etap 1 done, Etap 2 brakuje
    if (IN_GENERATION_STAGES.has(stage)) return 4; // w trakcie
    if (stage === "ready") return 5;          // wygenerowane, czeka na publish
    if (stage === "published") return 6;      // zakończone
    if (stage === "skipped_existing") return 7; // pominięte
    return 8;
  };

  // Broken-links priority sort — score = ile published artykułów linkuje do tego (unpublished) nodu.
  // Fetch raz na mount/mapId, plus refresh gdy user wybierze "broken_links" sort (cache 60s w service).
  // Map<nodeId, score> — nody bez broken links nie są w mapie (treat as 0).
  const [brokenLinksScores, setBrokenLinksScores] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (!mapId) return;
    let cancelled = false;
    getBrokenLinksScores(mapId).then((scores) => {
      if (!cancelled) setBrokenLinksScores(scores);
    }).catch(() => {
      // Silent — sort fallback to score=0 dla wszystkich (preserves "default" behavior)
    });
    return () => { cancelled = true; };
  }, [mapId, tableSortBy]);

  // Agregaty broken_links — do prezentacji w info card + button counter
  const brokenLinksStats = useMemo(() => {
    let nodesAffected = 0;
    let totalLinks = 0;
    let topScore = 0;
    const distribution = new Map<number, number>(); // score -> count
    for (const score of brokenLinksScores.values()) {
      if (score <= 0) continue;
      nodesAffected++;
      totalLinks += score;
      if (score > topScore) topScore = score;
      distribution.set(score, (distribution.get(score) ?? 0) + 1);
    }
    return { nodesAffected, totalLinks, topScore, distribution };
  }, [brokenLinksScores]);

  // Count "niewygenerowanych" — pending + failed + outline_ready (te które wymagają pracy).
  // NIE liczę in_progress (już lecą) ani ready/published/skipped (gotowe).
  const unfinishedCount = useMemo(() => {
    let count = 0;
    for (const node of nodes) {
      const stage = getEngineStage(node.id);
      if (stage === "pending" || stage === "failed" || stage === "outline_ready") count++;
    }
    return count;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, contentMap]);

  // Count nodów z gorszym QA score (< 100) — te do review/regen.
  // Liczę tylko te które mają quality_score (faktycznie audytowane). NULL = nie audytowane, pomijamy.
  const qaIssuesCount = useMemo(() => {
    let count = 0;
    for (const node of nodes) {
      const content = contentMap.get(node.id);
      const score = content?.quality_score;
      if (score != null && score < 100) count++;
    }
    return count;
  }, [nodes, contentMap]);

  // Filter + sort nodes
  const filteredNodes = useMemo(() => {
    const filtered = nodes.filter((node) => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!node.title.toLowerCase().includes(q) && !node.canonical_question.toLowerCase().includes(q)) {
          return false;
        }
      }
      // Status filter
      if (statusFilter !== "all") {
        const stage = getEngineStage(node.id);
        const c = contentMap.get(node.id);
        // "na WP" = wp_post_id/published_at (stage bywa "ready" mimo publikacji)
        const onWp = !!(c?.published_at || c?.wp_post_id) || stage === "published";
        if (statusFilter === "no_content" && stage !== "pending") return false;
        if (statusFilter === "outline_ready" && stage !== "outline_ready") return false;
        if (statusFilter === "in_progress" && !["serp_research", "kg_assembly", "fan_out", "outline", "draft", "enrichment", "optimization", "humanization"].includes(stage)) return false;
        if (statusFilter === "ready" && stage !== "ready") return false;
        if (statusFilter === "published" && !onWp) return false;
        if (statusFilter === "not_published" && onWp) return false;
        if (statusFilter === "failed" && stage !== "failed") return false;
      }
      return true;
    });
    // Sort widoku po kolumnie Seed lub broken-links score (view-only — NIE rusza batcha, ten ma swoja kolejnosc)
    if (tableSortBy === "broken_links") {
      // Priorytet: nody do których linkuje najwięcej opublikowanych artykułów. Tie-break: tytuł A→Z.
      return [...filtered].sort((a, b) => {
        const scoreA = brokenLinksScores.get(a.id) ?? 0;
        const scoreB = brokenLinksScores.get(b.id) ?? 0;
        if (scoreA !== scoreB) return scoreB - scoreA;
        return (a.title || "").localeCompare(b.title || "", "pl");
      });
    }
    if (tableSortBy === "unfinished_first") {
      // Niewygenerowane na górze: pending → failed → outline_ready → in_progress → ready → published → skipped.
      // Tie-break: tytuł A→Z.
      return [...filtered].sort((a, b) => {
        const rankA = getUnfinishedRank(getEngineStage(a.id));
        const rankB = getUnfinishedRank(getEngineStage(b.id));
        if (rankA !== rankB) return rankA - rankB;
        return (a.title || "").localeCompare(b.title || "", "pl");
      });
    }
    if (tableSortBy === "qa_score_asc") {
      // QA score ASC — najgorsze QA na górze. Nieaudytowane (null) → koniec.
      // Tie-break: tytuł A→Z.
      return [...filtered].sort((a, b) => {
        const scoreA = contentMap.get(a.id)?.quality_score;
        const scoreB = contentMap.get(b.id)?.quality_score;
        // null score → na koniec
        if (scoreA == null && scoreB == null) return (a.title || "").localeCompare(b.title || "", "pl");
        if (scoreA == null) return 1;
        if (scoreB == null) return -1;
        if (scoreA !== scoreB) return scoreA - scoreB; // ASC — niższe pierwsze
        return (a.title || "").localeCompare(b.title || "", "pl");
      });
    }
    if (tableSortBy !== "default") {
      const dir = tableSortBy === "seed_asc" ? 1 : -1;
      return [...filtered].sort((a, b) => {
        const aName = (a.seed_id && seedNameMap.get(a.seed_id)) || "zzz";
        const bName = (b.seed_id && seedNameMap.get(b.seed_id)) || "zzz";
        return aName.localeCompare(bName, "pl") * dir;
      });
    }
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, searchQuery, statusFilter, contentMap, tableSortBy, seedNameMap, brokenLinksScores]);

  // ============ Seed order panel (kolejnosc generowania w bulk batch) ============
  const [seedOrderExpanded, setSeedOrderExpanded] = useState(false);
  const [seedReordering, setSeedReordering] = useState(false);

  // Sorted seeds wg sort_order (sortowanie po klikach uzytkownika persisted w DB)
  const sortedSeeds = useMemo(() => {
    return [...seeds].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
  }, [seeds]);

  // Map seedId => index in sortedSeeds (uzywane do sortowania nodow w handleGenerateAll)
  const seedOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    sortedSeeds.forEach((s, i) => map.set(s.id, i));
    return map;
  }, [sortedSeeds]);

  // Per-seed counters dla UI listy
  const seedStats = useMemo(() => {
    const map = new Map<string, { total: number; todo: number }>();
    for (const seed of seeds) map.set(seed.id, { total: 0, todo: 0 });
    for (const n of nodes) {
      if (!n.seed_id) continue;
      const entry = map.get(n.seed_id);
      if (!entry) continue;
      entry.total++;
      const stage = getEngineStage(n.id);
      if (stage === "pending" || stage === "failed" || stage === "outline_ready") {
        entry.todo++;
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeds, nodes, contentMap]);

  // Swap sort_order miedzy seedem a sasiadem (atomic: 2 updates)
  const moveSeed = useCallback(async (seedId: string, direction: "up" | "down") => {
    if (seedReordering) return;
    const idx = sortedSeeds.findIndex((s) => s.id === seedId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sortedSeeds.length) return;

    const seedA = sortedSeeds[idx];
    const seedB = sortedSeeds[swapIdx];
    const orderA = seedA.sort_order ?? idx;
    const orderB = seedB.sort_order ?? swapIdx;

    setSeedReordering(true);
    try {
      // 2 updates atomic — jak ktoryjeden padnie, drugi nie pisze
      const okA = await updateSeed(seedA.id, { sort_order: orderB });
      if (!okA) throw new Error("Failed to update seed A");
      const okB = await updateSeed(seedB.id, { sort_order: orderA });
      if (!okB) throw new Error("Failed to update seed B");
      await refreshSeeds();
    } catch (err) {
      toast({
        title: "Blad zmiany kolejnosci",
        description: err instanceof Error ? err.message : "Unknown",
        variant: "destructive",
      });
    } finally {
      setSeedReordering(false);
    }
  }, [seedReordering, sortedSeeds, updateSeed, refreshSeeds, toast]);

  // ============ /Seed order panel ============

  // Stats
  const stats = useMemo(() => {
    const s = { total: nodes.length, pending: 0, outlineReady: 0, inProgress: 0, ready: 0, published: 0, failed: 0 };
    for (const node of nodes) {
      const stage = getEngineStage(node.id);
      if (stage === "pending") s.pending++;
      else if (stage === "outline_ready") s.outlineReady++;
      else if (stage === "ready") s.ready++;
      else if (stage === "published") s.published++;
      else if (stage === "failed") s.failed++;
      else s.inProgress++;
    }
    return s;
  }, [nodes, contentMap]);

  // ============= Centralny dispatcher single-node =============
  // Sprawdza czy node jest terminal (ready/published/skipped) — jesli tak,
  // pokazuje confirm modal force=true. Inaczej enqueue od razu.
  // W browser mode wola odpowiednia funkcje contentEngine bezposrednio.
  const dispatchSingleNode = useCallback(async (
    nodeId: string,
    stages: GenerationStages,
    onResult?: (r: { success: boolean; cost: number }) => void,
    onStage?: (stage: string) => void,
  ) => {
    const node = nodes.find((n) => n.id === nodeId);
    const nodeTitle = node?.title || nodeId.slice(0, 8);
    const content = contentMap.get(nodeId);
    const currentStage = content?.engine_stage ?? "pending";

    // GATE: pillar node (faza P) — pillary generujemy w SRO -> Pillar Page (nowy pillarGenerator), NIE tutaj.
    if (node && ((node as any).document_role === "pillar" || (node as any).node_role === "pillar")) {
      toast({
        title: `"${nodeTitle}" to pillar node`,
        description: 'Pillary generujemy w SRO -> Pillar Page (kropka "Pillar Page" w stepperze), NIE tutaj. Tam masz: Generuj, Re-generuj, Odswiez linki, Podglad.',
        variant: "destructive",
      });
      return;
    }

    // Terminal node + worker mode → confirm modal force=true.
    // Terminal node + browser mode → toast (uzyj Wyczysc tresc / Reset).
    const workerMode = await isWorkerModeEnabled();
    if (TERMINAL_STAGES.has(currentStage)) {
      if (workerMode) {
        setForceConfirm({ nodeId, nodeTitle, stages, content });
        return;
      }
      toast({
        title: `Node "${nodeTitle}" jest juz ${currentStage}`,
        description: 'Aby zregenerowac, kliknij "Wyczysc tresc" (RotateCcw) — zachowa outline. Lub wlacz Worker Mode w Ustawieniach (force=true z confirm).',
        variant: "destructive",
      });
      return;
    }

    if (workerMode) {
      try {
        const res = await enqueueGenerationJob(mapId, [nodeId], { stages });
        addLog("info", `→ Worker (${stages}): "${nodeTitle}"`);
        toast({
          title: "Wyslane do workera",
          description: `${nodeTitle} — ${stages === "structure" ? "struktura" : stages === "content" ? "tresc" : "pelny pipeline"} w tle.`,
        });
        return res;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Worker enqueue failed";
        addLog("error", `✗ Worker enqueue: "${nodeTitle}" — ${msg}`);
        toast({ title: "Blad workera", description: msg, variant: "destructive" });
        return;
      }
    }

    // Browser mode — bezposrednio
    // Race guard: blokuj jesli standalone aktualnie generuje (flag standaloneMode
    // w promptLoader jest globalna; bez locka mapa moglaby dostac
    // section_generation_standalone bez nakazu linkow).
    // Worker mode (pow.) omijamy — server-side, osobny proces, bez wspolnej flagi.
    const lock = tryAcquire("map_content");
    if (!lock.ok) {
      toast({
        title: "Generowanie zablokowane",
        description:
          lock.blockedBy === "standalone"
            ? "Trwa generowanie artykulu standalone — poczekaj na zakonczenie i sprobuj ponownie."
            : `Zablokowane przez aktywny proces: ${lock.blockedBy ?? "unknown"}.`,
        variant: "destructive",
      });
      return;
    }

    setGeneratingNodes((prev) => new Set(prev).add(nodeId));
    const stageLabel = stages === "structure" ? "Etap 1 — Struktura" : stages === "content" ? "Etap 2 — Tresc" : "Pelny pipeline";
    addLog("info", `▶ ${stageLabel}: "${nodeTitle}"`);

    // GeoNode proxy fallback status — UI-level log (gwarantuje widocznosc
    // w Dzienniku operacji niezaleznie od deep logPublic forwarding lub Vite cache)
    const proxyServer = env("VITE_GEONODE_PROXY_SERVER");
    const proxyUser = env("VITE_GEONODE_PROXY_USERNAME");
    const proxyPass = env("VITE_GEONODE_PROXY_PASSWORD");
    const proxyAvailable = !!proxyServer && !!proxyUser && !!proxyPass;
    if (proxyAvailable) {
      addLog("info", `🌐 GeoNode proxy fallback: ENABLED (server=${proxyServer})`);
    } else {
      addLog("error", `🌐 GeoNode proxy fallback: DISABLED — brak VITE_GEONODE_PROXY_* env w Coolify (Crawl4AI bedzie probowal tylko direct)`);
    }

    try {
      const fn = stages === "structure" ? generateStructure : stages === "content" ? generateContent : generateArticle;
      const result: any = await fn(nodeId, mapId, (progress: any) => {
        addLog("stage", `  ${progress.stageLabel}`, progress.stageDebug);
        if (progress.blockProgress) {
          addLog("info", `  Blok ${progress.blockProgress.current}/${progress.blockProgress.total}`);
        }
        if (progress.stageLabel) onStage?.(progress.stageLabel);
      });

      if (result.success) {
        const costSum = typeof result.cost?.total === "number"
          ? result.cost.total
          : Object.values(result.cost || {}).reduce((a: number, b: any) => a + (typeof b === "number" ? b : 0), 0);
        addLog("success", `✓ Gotowy (${stages}): "${nodeTitle}" — $${costSum.toFixed(3)} / ${(result.durationMs / 1000).toFixed(1)}s`);
        toast({ title: stages === "structure" ? "Struktura gotowa" : "Artykul wygenerowany", description: `${nodeTitle} — $${costSum.toFixed(3)}` });
        onResult?.({ success: true, cost: costSum });
      } else {
        addLog("error", `✗ Blad: "${nodeTitle}" — ${result.error}`);
        toast({ title: "Blad", description: result.error || "Nieznany blad", variant: "destructive" });
        onResult?.({ success: false, cost: 0 });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Nieznany blad";
      addLog("error", `✗ Exception: "${nodeTitle}" — ${msg}`);
      toast({ title: "Blad", description: msg, variant: "destructive" });
      onResult?.({ success: false, cost: 0 });
    } finally {
      setGeneratingNodes((prev) => { const n = new Set(prev); n.delete(nodeId); return n; });
      release("map_content");
    }
  }, [mapId, toast, nodes, contentMap, addLog]);

  // Single-node handlers — thin wrappers passing stages
  const handleGenerateOne = useCallback((nodeId: string) => dispatchSingleNode(nodeId, "full"), [dispatchSingleNode]);
  const handleGenerateStructure = useCallback((nodeId: string) => dispatchSingleNode(nodeId, "structure"), [dispatchSingleNode]);
  const handleGenerateContent = useCallback((nodeId: string) => dispatchSingleNode(nodeId, "content"), [dispatchSingleNode]);

  // Confirm modal callback — user potwierdzil regeneracje terminal node'a.
  // Worker dostaje force=true, hard- lub soft-resetuje wedlug stages.
  const handleForceConfirm = useCallback(async (target: RegenerateTarget) => {
    try {
      const res = await enqueueGenerationJob(mapId, [target.nodeId], { stages: target.stages, force: true });
      addLog("info", `→ Worker FORCE (${target.stages}): "${target.nodeTitle}" — stara wersja nadpisywana`);
      toast({
        title: "Regeneracja wystartowala",
        description: `${target.nodeTitle} — worker robi ${target.stages === "structure" ? "strukture" : target.stages === "content" ? "tresc" : "pelny pipeline"}, browser mozesz zamknac.`,
      });
      setForceConfirm(null);
      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Worker enqueue failed";
      toast({ title: "Blad workera", description: msg, variant: "destructive" });
    }
  }, [mapId, addLog, toast]);

  // Generate selected
  const handleGenerateSelected = useCallback(async () => {
    const nodeIds = [...selectedNodes];
    for (const nodeId of nodeIds) {
      await handleGenerateOne(nodeId);
    }
    setSelectedNodes(new Set());
  }, [selectedNodes, handleGenerateOne]);

  // Generate all pending (full) — wyklucza node'y juz mid-pipeline (race-safe)
  // JEDEN PRZYCISK: zgarnia pending/failed (full pipeline) + outline_ready (resume Etap 2)
  // POMIJA: ready, published, in-progress stages (race-safe).
  // batchLimitInput: jesli wpisane N -> bierze tylko pierwsze N od gory (wedlug DB order).
  // Confirm dialog z cost/ETA + warningi (browser open / no sleep / continues on error)
  const handleGenerateAll = useCallback(async () => {
    // Bucket 1: pending/failed → full pipeline (Etap 1 + Etap 2)
    const allFullNodes = nodes.filter((n) => {
      const stage = getEngineStage(n.id);
      return stage === "pending" || stage === "failed";
    });
    // Bucket 2: outline_ready → resume Etap 2 only (outline juz wygenerowany)
    const allResumeNodes = nodes.filter((n) => getEngineStage(n.id) === "outline_ready");

    const totalAvailable = allFullNodes.length + allResumeNodes.length;
    if (totalAvailable === 0) {
      toast({
        title: "Brak nodów do generowania",
        description: "Wszystkie sa juz gotowe (ready/published) lub w trakcie. Pominieto ready/published — NIE bedzie regeneracji.",
      });
      return;
    }

    // Apply batchLimit if user typed N (puste = bez limitu)
    const parsedLimit = parseInt(batchLimitInput.trim(), 10);
    const hasLimit = !isNaN(parsedLimit) && parsedLimit > 0;
    const limit = hasLimit ? Math.min(parsedLimit, totalAvailable) : totalAvailable;

    // Order: respektuj kolejnosc SORTOWANIA TABELI (klik header "Seed" w tabeli).
    // - tableSortBy = "default" → DB order (jak nodes wczytane)
    // - tableSortBy = "seed_asc" → seed name A→Z (po seedNameMap)
    // - tableSortBy = "seed_desc" → seed name Z→A
    // - tableSortBy = "broken_links" → broken_link_score DESC (SRO 11 Closed Loop priority)
    // Nody bez seed_id leca na koniec (alfabetycznie "zzz"). Nody bez score lecą jako 0.
    let sortedFullNodes: typeof allFullNodes;
    let sortedResumeNodes: typeof allResumeNodes;
    if (tableSortBy === "default") {
      sortedFullNodes = allFullNodes;
      sortedResumeNodes = allResumeNodes;
    } else if (tableSortBy === "broken_links") {
      const sortByBrokenLinks = (a: { id: string; title?: string | null }, b: { id: string; title?: string | null }) => {
        const scoreA = brokenLinksScores.get(a.id) ?? 0;
        const scoreB = brokenLinksScores.get(b.id) ?? 0;
        if (scoreA !== scoreB) return scoreB - scoreA;
        return (a.title || "").localeCompare(b.title || "", "pl");
      };
      sortedFullNodes = [...allFullNodes].sort(sortByBrokenLinks);
      sortedResumeNodes = [...allResumeNodes].sort(sortByBrokenLinks);
    } else if (tableSortBy === "unfinished_first") {
      const sortByUnfinished = (a: { id: string; title?: string | null }, b: { id: string; title?: string | null }) => {
        const rankA = getUnfinishedRank(getEngineStage(a.id));
        const rankB = getUnfinishedRank(getEngineStage(b.id));
        if (rankA !== rankB) return rankA - rankB;
        return (a.title || "").localeCompare(b.title || "", "pl");
      };
      sortedFullNodes = [...allFullNodes].sort(sortByUnfinished);
      sortedResumeNodes = [...allResumeNodes].sort(sortByUnfinished);
    } else if (tableSortBy === "qa_score_asc") {
      const sortByQaScore = (a: { id: string; title?: string | null }, b: { id: string; title?: string | null }) => {
        const scoreA = contentMap.get(a.id)?.quality_score;
        const scoreB = contentMap.get(b.id)?.quality_score;
        if (scoreA == null && scoreB == null) return (a.title || "").localeCompare(b.title || "", "pl");
        if (scoreA == null) return 1;
        if (scoreB == null) return -1;
        if (scoreA !== scoreB) return scoreA - scoreB;
        return (a.title || "").localeCompare(b.title || "", "pl");
      };
      sortedFullNodes = [...allFullNodes].sort(sortByQaScore);
      sortedResumeNodes = [...allResumeNodes].sort(sortByQaScore);
    } else {
      const dir = tableSortBy === "seed_asc" ? 1 : -1;
      const sortBySeedName = (a: { seed_id?: string | null }, b: { seed_id?: string | null }) => {
        const aName = (a.seed_id && seedNameMap.get(a.seed_id)) || "zzz";
        const bName = (b.seed_id && seedNameMap.get(b.seed_id)) || "zzz";
        return aName.localeCompare(bName, "pl") * dir;
      };
      sortedFullNodes = [...allFullNodes].sort(sortBySeedName);
      sortedResumeNodes = [...allResumeNodes].sort(sortBySeedName);
    }

    // Order: fullNodes first, then resumeNodes (slice po N preserves seed order)
    const fullNodes = sortedFullNodes.slice(0, limit);
    const resumeNodes = sortedResumeNodes.slice(0, Math.max(0, limit - fullNodes.length));
    const total = fullNodes.length + resumeNodes.length;

    const workerMode = await isWorkerModeEnabled();
    const estimatedCost = fullNodes.length * 4 + resumeNodes.length * 3.85; // resume tansze (skip Etap 1)
    const estimatedHours = Math.max(1, Math.round((fullNodes.length * 12 + resumeNodes.length * 10) / 60));

    if (!workerMode) {
      const limitNote = hasLimit ? `\n\n>>> LIMIT: bierze tylko pierwsze ${total} z ${totalAvailable} dostepnych <<<` : "";
      if (!confirm(
        `Wygenerowac ${hasLimit ? total : "WSZYSTKO niegotowe"}?${limitNote}\n\n` +
        `* ${fullNodes.length} nodow PEŁNY PIPELINE (pending/failed -> Etap 1 + Etap 2)\n` +
        `* ${resumeNodes.length} nodow RESUME (outline_ready -> tylko Etap 2)\n` +
        `* RAZEM: ${total} nodow${hasLimit ? ` (z ${totalAvailable} dostepnych)` : ""}\n\n` +
        `POMIJANE: ready, published, in-progress (race-safe)\n` +
        `Oczekiwany koszt: ~$${estimatedCost.toFixed(0)}\n` +
        `Oczekiwany czas: ~${estimatedHours}h sequential\n\n` +
        `WAZNE:\n` +
        `* Karta przegladarki MUSI pozostac otwarta\n` +
        `* Komputer NIE moze usypiac (Power Plan: never sleep)\n` +
        `* Error w jednym nodzie -> continues do nastepnego\n` +
        `* Brak limitow dziennych — leci do konca lub do crashu\n\n` +
        `Kontynuowac?`
      )) return;
    }

    if (workerMode) {
      try {
        // Worker dostaje obydwa buckety — generationProcessor sam wybierze stages
        // (jesli outline_ready -> resume, inaczej full)
        const allIds = [...fullNodes.map((n) => n.id), ...resumeNodes.map((n) => n.id)];
        const res = await enqueueGenerationJob(mapId, allIds, { stages: "full" });
        addLog("info", `→ Worker batch: ${res.enqueued} node'ow (${fullNodes.length} full + ${resumeNodes.length} resume)${hasLimit ? ` [LIMIT ${parsedLimit}]` : ""}`);
        toast({ title: "Wyslane do workera", description: `${res.enqueued} node'ow. Mozesz zamknac browser.` });
      } catch (err) {
        toast({ title: "Blad workera", description: err instanceof Error ? err.message : "Worker enqueue failed", variant: "destructive" });
      }
      return;
    }

    // Parse concurrency (1-5 clamp)
    const parsedConc = parseInt(batchConcurrencyInput.trim(), 10);
    const concurrency = !isNaN(parsedConc) ? Math.max(1, Math.min(5, parsedConc)) : 2;

    addLog("info", `▶ Batch JEDEN PRZYCISK — ${total} node'ow${hasLimit ? ` (LIMIT ${parsedLimit} z ${totalAvailable})` : ""} (${fullNodes.length} full + ${resumeNodes.length} resume), ~$${estimatedCost.toFixed(0)}, ~${estimatedHours}h, concurrency=${concurrency}, hard cap ${Math.round(NODE_HARD_TIMEOUT_MS / 60_000)}min/node, stuck-detect ${Math.round(STAGE_STALE_THRESHOLD_MS / 60_000)}min bez callbacku`);

    // GeoNode proxy status na starcie batch'a — gwarantuje widocznosc w UI Dzienniku
    {
      const ps = env("VITE_GEONODE_PROXY_SERVER");
      const pu = env("VITE_GEONODE_PROXY_USERNAME");
      const pp = env("VITE_GEONODE_PROXY_PASSWORD");
      if (ps && pu && pp) {
        addLog("info", `🌐 GeoNode proxy fallback: ENABLED dla calego batcha (server=${ps})`);
      } else {
        addLog("error", `🌐 GeoNode proxy fallback: DISABLED — brak VITE_GEONODE_PROXY_* env w Coolify`);
      }
    }

    // ============ Setup safeguards state ============
    setBatchRunning(true);
    setBatchPaused(false);
    setFloatingMinimized(false);
    setFloatingDismissed(false);
    setLiveBatch({
      current: 0,
      total,
      activeNodes: [],
      etaMin: estimatedHours * 60,
      failed: 0,
      skipped: 0,
      errored: 0,
      secsSinceLastActivity: 0,
      concurrency,
    });
    batchStartTsRef.current = Date.now();
    lastProgressTsRef.current = Date.now();
    pauseRequestedRef.current = false;
    guardTriggeredRef.current = false;
    consecutiveFailsRef.current = 0;
    consecutiveHardErrorsRef.current = 0;
    recentErrorsRef.current = [];
    totalBatchRef.current = total;
    activeNodesRef.current.clear();
    heartbeatLogTsRef.current = Date.now();

    // Best-effort: poproś o uprawnienia desktop notification
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      try { await Notification.requestPermission(); } catch { /* ignore */ }
    }

    let processed = 0;
    let failed = 0;   // result.success=false (legit prompt-level)
    let skipped = 0;  // hard timeout 35min
    let errored = 0;  // exception/crash/network (systemowe)
    let abortedBySpiral = false;

    const pushRecentError = (title: string, reason: string) => {
      recentErrorsRef.current.push({ title, reason, ts: Date.now() });
      if (recentErrorsRef.current.length > 3) {
        recentErrorsRef.current.shift();
      }
    };

    const noteHardError = (title: string, reason: string) => {
      consecutiveHardErrorsRef.current++;
      pushRecentError(title, reason);
    };

    const waitWhilePaused = async (): Promise<void> => {
      while (pauseRequestedRef.current) {
        await new Promise((r) => setTimeout(r, 1000));
      }
    };

    const updateLiveCounts = () => {
      const elapsedMin = (Date.now() - batchStartTsRef.current) / 60_000;
      const done = processed + failed + skipped + errored;
      const avgPerNode = done > 0 ? elapsedMin / done : 12;
      const remaining = Math.max(0, totalBatchRef.current - done);
      setLiveBatch((s) => ({
        ...s,
        current: done,
        etaMin: Math.max(0, Math.round((remaining * avgPerNode) / concurrency)),
        failed,
        skipped,
        errored,
      }));
    };

    // Per-node STALE watchdog — skip node jesli stage callback NIE wpadl przez
    // STAGE_STALE_THRESHOLD_MS (= 18 min). Jak pipeline regularnie wysyla
    // progress, leci dowolnie dlugo. Tylko zacinanie na JEDNYM stagu = skip.
    const dispatchWithStaleWatchdog = async (nodeId: string, stages: GenerationStages, title: string): Promise<"ok" | "fail" | "skip"> => {
      const startTs = Date.now();
      activeNodesRef.current.set(nodeId, { title, stage: "start", startTs });
      lastProgressTsRef.current = Date.now();

      // Per-node last callback ts — updated by onStage + onResult
      let lastCallbackTs = Date.now();
      let nodeOk = false;
      let nodeReceived = false;

      const dispatch = dispatchSingleNode(
        nodeId,
        stages,
        (r) => {
          nodeReceived = true;
          nodeOk = r.success;
          lastCallbackTs = Date.now();
          lastProgressTsRef.current = Date.now();
          if (r.success) {
            processed++;
            consecutiveHardErrorsRef.current = 0;
          } else {
            failed++;
            // failed (result.success=false) NIE liczy do anti-spirala
          }
          activeNodesRef.current.delete(nodeId);
          updateLiveCounts();
        },
        (stage) => {
          lastCallbackTs = Date.now();
          lastProgressTsRef.current = Date.now();
          const cur = activeNodesRef.current.get(nodeId);
          if (cur && cur.stage !== stage) {
            activeNodesRef.current.set(nodeId, { ...cur, stage });
          }
        },
      );

      // Per-node stale watcher — co 30s sprawdza czy callback nie wpadl dawno
      let staleIntervalId: ReturnType<typeof setInterval> | null = null;
      const stalePromise = new Promise<"stale">((resolve) => {
        staleIntervalId = setInterval(() => {
          if (nodeReceived) return;
          const silenceMs = Date.now() - lastCallbackTs;
          if (silenceMs > STAGE_STALE_THRESHOLD_MS) {
            if (staleIntervalId) clearInterval(staleIntervalId);
            resolve("stale");
          }
        }, STALE_CHECK_INTERVAL_MS);
      });
      // Hard cap — 30 min absolute max niezaleznie od callbackow
      const hardCapPromise = new Promise<"hardcap">((resolve) => {
        setTimeout(() => resolve("hardcap"), NODE_HARD_TIMEOUT_MS);
      });

      const winner = await Promise.race([
        dispatch.then(() => "done" as const),
        stalePromise,
        hardCapPromise,
      ]);
      if (staleIntervalId) clearInterval(staleIntervalId);

      if ((winner === "stale" || winner === "hardcap") && !nodeReceived) {
        const totalMin = Math.round((Date.now() - startTs) / 60_000);
        const silentMin = Math.round((Date.now() - lastCallbackTs) / 60_000);
        const lastStage = activeNodesRef.current.get(nodeId)?.stage ?? "?";
        const reasonLog = winner === "hardcap"
          ? `⏱ HARD CAP 30min — node "${title}" przekroczyl absolutny limit (cisza ${silentMin}min, stage "${lastStage}"), pomijam.`
          : `⏱ STUCK ${silentMin}min bez progressu na stagu "${lastStage}" — node "${title}" pomijam, lece dalej.`;
        addLog("error", reasonLog);
        skipped++;
        noteHardError(title, winner === "hardcap"
          ? `hard cap ${totalMin}min`
          : `stuck ${silentMin}min na "${lastStage}"`);
        lastProgressTsRef.current = Date.now();
        activeNodesRef.current.delete(nodeId);
        updateLiveCounts();
        return "skip";
      }
      return nodeOk ? "ok" : "fail";
    };

    // Build shared queue: full pipeline jobs + resume jobs
    type BatchJob = { node: { id: string; title?: string }; stages: GenerationStages };
    const queue: BatchJob[] = [
      ...fullNodes.map((n) => ({ node: n, stages: "full" as GenerationStages })),
      ...resumeNodes.map((n) => ({ node: n, stages: "content" as GenerationStages })),
    ];

    // Concurrent worker pool — concurrency workers konsumuja z shared queue
    let queueIdx = 0;
    const claimJob = (): BatchJob | null => {
      // Anti-spiral: tylko HARD errors (timeout + crash) — NIE failed (result.success=false legit)
      if (consecutiveHardErrorsRef.current >= BATCH_MAX_HARD_ERRORS && !pauseRequestedRef.current) {
        abortedBySpiral = true;
        pauseRequestedRef.current = true;
        setBatchPaused(true);
        const errorsList = recentErrorsRef.current
          .map((e, i) => `${i + 1}. "${e.title.slice(0, 40)}" — ${e.reason}`)
          .join("\n");
        addLog("error", `⚠ Anti-spiral: ${consecutiveHardErrorsRef.current} HARD bledow z rzedu (timeout/crash) => AUTO-PAUZA.`);
        addLog("error", `   Ostatnie ${recentErrorsRef.current.length}:`);
        recentErrorsRef.current.forEach((e, i) => {
          addLog("error", `   ${i + 1}. "${e.title.slice(0, 60)}" — ${e.reason}`);
        });
        toast({
          title: "Pauza: zbyt wiele hard bledow",
          description: `${consecutiveHardErrorsRef.current} z rzedu (timeout/crash):\n${errorsList}`,
          variant: "destructive",
          duration: 60_000,
        });
      }
      if (queueIdx >= queue.length) return null;
      return queue[queueIdx++];
    };

    const workerLoop = async (workerIdx: number) => {
      while (true) {
        await waitWhilePaused();
        if (pauseRequestedRef.current && abortedBySpiral) break;
        const job = claimJob();
        if (!job) break;
        const title = job.node.title || job.node.id.slice(0, 8);
        addLog("info", `🚀 Worker ${workerIdx + 1}: start "${title}" (${job.stages})`);
        let hadHardError = false;
        try {
          const outcome = await dispatchWithStaleWatchdog(job.node.id, job.stages, title);
          if (outcome === "skip") {
            hadHardError = true; // stuck stage = hard error
          }
        } catch (err) {
          // Exception/crash = HARD error (systemowe, np. network down, broken module)
          errored++;
          const reason = err instanceof Error ? err.message : "Unknown crash";
          noteHardError(title, `crash: ${reason.slice(0, 60)}`);
          addLog("error", `✗ Worker ${workerIdx + 1}: "${title}" crashed — ${reason}`);
          activeNodesRef.current.delete(job.node.id);
          updateLiveCounts();
          hadHardError = true;
        }
        // Cooldown po hard error — daj LLM API oddechu, zmniejsz burst
        if (hadHardError && POST_ERROR_COOLDOWN_MS > 0) {
          addLog("info", `   ⏸ Cooldown ${POST_ERROR_COOLDOWN_MS / 1000}s po hard error...`);
          await new Promise((r) => setTimeout(r, POST_ERROR_COOLDOWN_MS));
        }
      }
    };

    addLog("info", `⚙ Concurrency: ${concurrency} workers w parallel`);
    await Promise.all(
      Array.from({ length: concurrency }, (_, i) => workerLoop(i))
    );

    setBatchRunning(false);
    setBatchPaused(false);
    pauseRequestedRef.current = false;

    const totalBad = failed + skipped + errored;
    const titleStr = abortedBySpiral
      ? "Batch przerwany — anti-spiral"
      : totalBad > 0
        ? "Batch zakonczony (z problemami)"
        : "Batch zakonczony";
    const summary = `OK ${processed}, fail ${failed}, timeout ${skipped}, crash ${errored} z ${total}`;
    addLog(totalBad > 0 || abortedBySpiral ? "error" : "success", `■ ${titleStr}: ${summary}`);

    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      try {
        new Notification(titleStr, {
          body: summary,
          icon: "/favicon.ico",
        });
      } catch { /* ignore */ }
    }

    toast({
      title: titleStr,
      description: `✓ ${processed} | ✗ fail ${failed} | ⏱ timeout ${skipped} | 💥 crash ${errored} z ${total}`,
      variant: totalBad > total / 4 || abortedBySpiral ? "destructive" : "default",
      duration: 15_000,
    });
  }, [nodes, dispatchSingleNode, mapId, toast, addLog, batchLimitInput, batchConcurrencyInput, tableSortBy, seedNameMap, brokenLinksScores]);

  // Stop / Wznow / Cofnij okno
  const handleStopBatch = useCallback(() => {
    if (!batchRunning) return;
    pauseRequestedRef.current = true;
    setBatchPaused(true);
    addLog("info", "⏸ Pauza manualna. Klik 'Wznow' by kontynuowac.");
  }, [batchRunning, addLog]);

  const handleResumeBatch = useCallback(() => {
    if (!batchRunning) return;
    pauseRequestedRef.current = false;
    guardTriggeredRef.current = false;
    consecutiveFailsRef.current = 0;
    lastProgressTsRef.current = Date.now();
    setBatchPaused(false);
    addLog("info", "▶ Wznowiono. Spiral counter zresetowany.");
  }, [batchRunning, addLog]);

  // Batch: Etap 1 for all pending (+ failed do retry)
  const handleBatchStructure = useCallback(async () => {
    const pendingNodes = nodes.filter((n) => {
      const stage = getEngineStage(n.id);
      return stage === "pending" || stage === "failed";
    });
    if (pendingNodes.length === 0) {
      toast({ title: "Brak nodów", description: "Wszystkie maja juz strukture lub sa w trakcie." });
      return;
    }
    addLog("info", `▶ Batch Etap 1 — ${pendingNodes.length} node'ów`);
    if (await isWorkerModeEnabled()) {
      try {
        const res = await enqueueGenerationJob(mapId, pendingNodes.map((n) => n.id), { stages: "structure" });
        addLog("info", `→ Worker batch (structure): ${res.enqueued} node'ów w tle`);
        toast({ title: "Wyslane do workera", description: `${res.enqueued} node'ów. Mozesz zamknac browser.` });
      } catch (err) {
        toast({ title: "Blad workera", description: err instanceof Error ? err.message : "Worker enqueue failed", variant: "destructive" });
      }
      return;
    }
    for (const node of pendingNodes) {
      await handleGenerateStructure(node.id);
    }
  }, [nodes, handleGenerateStructure, mapId, toast, addLog]);

  // Batch: Etap 2 for all outline_ready
  const handleBatchContent = useCallback(async () => {
    const readyNodes = nodes.filter((n) => getEngineStage(n.id) === "outline_ready");
    if (readyNodes.length === 0) {
      toast({ title: "Brak nodów", description: "Brak nodów ze strukturą gotową do Etapu 2." });
      return;
    }
    addLog("info", `▶ Batch Etap 2 — ${readyNodes.length} node'ów`);
    if (await isWorkerModeEnabled()) {
      try {
        const res = await enqueueGenerationJob(mapId, readyNodes.map((n) => n.id), { stages: "content" });
        addLog("info", `→ Worker batch (content): ${res.enqueued} node'ów w tle`);
        toast({ title: "Wyslane do workera", description: `${res.enqueued} node'ów. Mozesz zamknac browser.` });
      } catch (err) {
        toast({ title: "Blad workera", description: err instanceof Error ? err.message : "Worker enqueue failed", variant: "destructive" });
      }
      return;
    }
    for (const node of readyNodes) {
      await handleGenerateContent(node.id);
    }
  }, [nodes, handleGenerateContent, mapId, toast, addLog]);

  // Toggle outline preview
  const toggleOutline = (nodeId: string) => {
    setExpandedOutlines((prev) => {
      const n = new Set(prev);
      if (n.has(nodeId)) n.delete(nodeId); else n.add(nodeId);
      return n;
    });
  };

  // Clear SERP cache for this map
  const handleClearCache = useCallback(async () => {
    try {
      const nodeIds = nodes.map((n) => n.id);
      if (nodeIds.length === 0) return;

      const { error } = await (supabase as any)
        .from("content_serp_cache")
        .delete()
        .in("node_id", nodeIds);

      if (error) throw error;

      addLog("info", `🗑 Cache SERP wyczyszczony dla ${nodeIds.length} node'ów — kolejne generowanie pobierze świeże dane`);
      toast({ title: "Cache wyczyszczony", description: `Usunięto cache SERP dla ${nodeIds.length} node'ów` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Nieznany błąd";
      addLog("error", `✗ Błąd czyszczenia cache: ${msg}`);
      toast({ title: "Błąd", description: msg, variant: "destructive" });
    }
  }, [nodes, addLog, toast]);

  // Clear content only — keep outline, revert to outline_ready
  const handleClearContent = useCallback(async (nodeId: string) => {
    try {
      await (supabase as any)
        .from("thematic_node_content")
        .update({
          engine_stage: "outline_ready",
          draft_html: null,
          enriched_html: null,
          optimized_html: null,
          final_html: null,
          qa_status: null,
          quality_score: null,
          content_status: "empty",
        })
        .eq("node_id", nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      addLog("info", `🔄 Treść wyczyszczona: "${node?.title || nodeId.slice(0, 8)}" — struktura zachowana`);
      toast({ title: "Treść wyczyszczona", description: "Struktura (H2/H3) zachowana. Możesz wygenerować treść ponownie." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd";
      toast({ title: "Błąd", description: msg, variant: "destructive" });
    }
  }, [nodes, addLog, toast]);

  // Reset node from outline_ready back to pending
  const handleDeleteStructure = useCallback(async (nodeId: string) => {
    try {
      await (supabase as any)
        .from("thematic_node_content")
        .update({
          engine_stage: "pending",
          outline: null,
          fan_out: null,
          distribution: null,
          engine_debug: null,
          engine_cost: null,
        })
        .eq("node_id", nodeId);

      // Also delete KG for this node
      await (supabase as any)
        .from("content_knowledge_graphs")
        .delete()
        .eq("node_id", nodeId);

      const node = nodes.find((n) => n.id === nodeId);
      addLog("info", `🗑 Struktura usunięta: "${node?.title || nodeId.slice(0, 8)}"`);
      toast({ title: "Struktura usunięta", description: "Node zresetowany do stanu początkowego" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd";
      toast({ title: "Błąd", description: msg, variant: "destructive" });
    }
  }, [nodes, addLog, toast]);

  // Reset skip status (skipped_existing → pending lub locked)
  // Wywoływane gdy wp_matching audit dopasował nod do słabego/nieistniejącego WP postu
  // i user chce wymusić regeneracja albo świadomie zablokować skip.
  const handleResetSkip = useCallback(async (nodeId: string, mode: "regenerate" | "skip_manual") => {
    const node = nodes.find((n) => n.id === nodeId);
    const title = node?.title || nodeId.slice(0, 8);

    const confirmMsg = mode === "regenerate"
      ? `Odblokować "${title}"?\n\nNod był pominięty przez audit wp_matching (znaleziony istniejący WP post). Odblokowanie zresetuje status → pipeline wygeneruje NOWY artykuł w następnym batchu.\n\nUwaga: stary WP post zostaje na stronie — może być potrzeba ręcznego usunięcia/redirectu.`
      : `Zablokować "${title}" jako skip ręczny?\n\nPipeline NIGDY nie będzie regenerował tego nodu (status: skip_manual). Możesz odblokować później tym samym przyciskiem.`;

    if (!window.confirm(confirmMsg)) return;

    const result = await resetNodeSkipStatus(nodeId, mode);
    if (result.success) {
      addLog("success", `${mode === "regenerate" ? "🔓" : "🔒"} ${result.message}: "${title}"`);
      toast({ title: "Status zmieniony", description: result.message });
      // Trigger refetch — useThematicNodeContent + useThematicNodes powinny dostać realtime update
      // Fallback: window.location.reload() jeśli realtime nie złapie zmian (engine_stage zmiana)
    } else {
      addLog("error", `Reset skip nie powiódł się: ${result.message}`);
      toast({ title: "Błąd", description: result.message, variant: "destructive" });
    }
  }, [nodes, addLog, toast]);

  // Selection
  const toggleSelect = (nodeId: string) => {
    setSelectedNodes((prev) => {
      const n = new Set(prev);
      if (n.has(nodeId)) n.delete(nodeId); else n.add(nodeId);
      return n;
    });
  };
  const toggleSelectAll = () => {
    if (selectedNodes.size === filteredNodes.length) {
      setSelectedNodes(new Set());
    } else {
      setSelectedNodes(new Set(filteredNodes.map((n) => n.id)));
    }
  };

  return (
    <div className="space-y-5">
      {/* Header z inline heatmap (bez redundant tekstu "Etap 1: SERP -> KG...") */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Content Engine — Produkcja artykułów
          </h2>
          <LiveIndicator isLive={contentsLive} label="Live" className="mt-2" />
        </div>
        {/* Pipeline breakdown inline — zastepuje stary opis "Etap 1: SERP -> KG..." */}
        <WidgetErrorBoundary name="StageDistributionHeatmap" fallback={null}>
          <StageDistributionHeatmap
            nodes={nodes}
            contentMap={contentMap}
            onSegmentClick={(filterValue) => setStatusFilter(filterValue)}
            variant="compact"
          />
        </WidgetErrorBoundary>
      </div>

      {/* ============================================================ */}
      {/* PROMINENT BATCH BANNER — JEDEN PRZYCISK na gorze, kolorowy   */}
      {/* ============================================================ */}
      {(stats.pending + stats.outlineReady + stats.failed) > 0 && (() => {
        const totalAvail = stats.pending + stats.outlineReady + stats.failed;
        const parsed = parseInt(batchLimitInput.trim(), 10);
        const lim = !isNaN(parsed) && parsed > 0 ? Math.min(parsed, totalAvail) : totalAvail;
        const estCost = Math.round(lim * 4);
        return (
          <div className="rounded-xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 via-emerald-500/8 to-transparent p-5 shadow-md">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px] space-y-1.5">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-emerald-500" />
                  <h3 className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                    Wygeneruj artykuły — JEDEN PRZYCISK
                  </h3>
                </div>
                <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span><span className="font-semibold text-foreground">{totalAvail}</span> do zrobienia</span>
                  <span className="text-muted-foreground/50">•</span>
                  <span><span className="font-semibold text-emerald-600 dark:text-emerald-400">{stats.ready + stats.published}</span> juz gotowe (pominiete)</span>
                  <span className="text-muted-foreground/50">•</span>
                  <span>~${estCost} oczekiwany koszt</span>
                </div>
                <p className="text-xs text-muted-foreground/80">
                  POMIJA ready/published. Bierze pending+failed (full pipeline) + outline_ready (resume Etap 2).
                  Wpisz <span className="font-semibold">N</span> w polu obok zeby wziac tylko pierwsze N nodow.
                </p>

                {/* Seed order panel — UKRYTY (sort kolejnosci jest teraz na tabeli przez klikalny header "Seed").
                    Kod zachowany, gotowy do odkomentowania jak by sie przydal. */}
                {false && sortedSeeds.length > 1 && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setSeedOrderExpanded((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-xs font-semibold text-emerald-700 dark:text-emerald-300 transition-colors"
                    >
                      <span className="text-base leading-none">{seedOrderExpanded ? "▾" : "▸"}</span>
                      <span>USTAW KOLEJNOSC SEEDOW ({sortedSeeds.length})</span>
                      <span className="text-emerald-600/70 dark:text-emerald-400/70 font-normal">
                        — batch leci #1 → #{sortedSeeds.length}
                      </span>
                    </button>
                    {seedOrderExpanded && (
                      <div className="mt-2 space-y-1 rounded border border-emerald-500/20 bg-background/40 p-2 max-h-64 overflow-auto">
                        {sortedSeeds.map((seed, idx) => {
                          const stats = seedStats.get(seed.id) ?? { total: 0, todo: 0 };
                          const isFirst = idx === 0;
                          const isLast = idx === sortedSeeds.length - 1;
                          return (
                            <div
                              key={seed.id}
                              className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-muted/40 transition-colors"
                            >
                              <span className="font-mono text-muted-foreground w-6 text-right">#{idx + 1}</span>
                              <span className="flex-1 truncate font-medium" title={seed.name}>
                                {seed.name}
                              </span>
                              <span className="text-muted-foreground font-mono text-[10px]">
                                {stats.todo > 0 ? (
                                  <span className="text-amber-600 dark:text-amber-400">{stats.todo} do zrobienia</span>
                                ) : (
                                  <span className="text-emerald-600 dark:text-emerald-400">✓ wszystko gotowe</span>
                                )}
                                <span className="text-muted-foreground/60"> / {stats.total} total</span>
                              </span>
                              <div className="flex items-center gap-0.5 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => moveSeed(seed.id, "up")}
                                  disabled={isFirst || seedReordering || batchRunning}
                                  className="h-6 w-6 rounded hover:bg-emerald-500/15 disabled:opacity-30 disabled:hover:bg-transparent text-emerald-700 dark:text-emerald-400 flex items-center justify-center"
                                  title="W gore"
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveSeed(seed.id, "down")}
                                  disabled={isLast || seedReordering || batchRunning}
                                  className="h-6 w-6 rounded hover:bg-emerald-500/15 disabled:opacity-30 disabled:hover:bg-transparent text-emerald-700 dark:text-emerald-400 flex items-center justify-center"
                                  title="W dol"
                                >
                                  ↓
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        <p className="text-[10px] text-muted-foreground/70 pt-1 border-t border-border/50 mt-2">
                          Batch generuje nody w tej kolejnosci. Worker pool ({batchConcurrencyInput || 2}) procesuje
                          rownolegle w obrebie tego samego seeda; nastepny seed po wyczerpaniu poprzedniego.
                          {batchRunning && " Zablokowane podczas trwajacego batcha."}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                    Ile od gory
                  </label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="wszystkie"
                    value={batchLimitInput}
                    onChange={(e) => setBatchLimitInput(e.target.value)}
                    disabled={batchRunning}
                    className="h-11 w-28 text-sm font-medium border-emerald-500/30"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold"
                    title="Ile artykulow rownolegle (1-5). Wieksze = szybsze, ale wiekszy burst LLM calls i risk 429."
                  >
                    Rownolegle
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={batchConcurrencyInput}
                    onChange={(e) => setBatchConcurrencyInput(e.target.value)}
                    disabled={batchRunning}
                    className="h-11 w-20 text-sm font-medium border-emerald-500/30 text-center"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold">
                    Akcja
                  </label>
                  <Button
                    onClick={handleGenerateAll}
                    disabled={batchRunning}
                    size="lg"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md hover:shadow-lg transition-shadow font-semibold h-11 px-6"
                    title="Pelny pipeline + resume Etap 2 dla outline_ready. POMIJA ready/published — NIE regeneruje gotowych."
                  >
                    <Zap className="mr-2 h-4 w-4" />
                    {batchRunning
                      ? `Generuje ${liveBatch.current}/${liveBatch.total}`
                      : `Wygeneruj ${lim === totalAvail ? `WSZYSTKO (${totalAvail})` : `${lim} z ${totalAvail}`}`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Stats bar */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold">{stats.total}</p>
          <p className="text-xs text-muted-foreground">Łącznie</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-muted-foreground">{stats.pending}</p>
          <p className="text-xs text-muted-foreground">Bez treści</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-violet-600 dark:text-violet-400">{stats.outlineReady}</p>
          <p className="text-xs text-muted-foreground">Struktura ✓</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.inProgress}</p>
          <p className="text-xs text-muted-foreground">W trakcie</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.ready}</p>
          <p className="text-xs text-muted-foreground">Gotowe</p>
        </div>
        <div className="border rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.failed}</p>
          <p className="text-xs text-muted-foreground">Błędy</p>
        </div>
      </div>

      {/* Actions bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Szukaj node..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          {/* Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <Filter className="h-3.5 w-3.5 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie</SelectItem>
              <SelectItem value="no_content">Bez treści</SelectItem>
              <SelectItem value="outline_ready">Struktura gotowa</SelectItem>
              <SelectItem value="in_progress">W trakcie</SelectItem>
              <SelectItem value="ready">Gotowe</SelectItem>
              <SelectItem value="published">Opublikowane na WP</SelectItem>
              <SelectItem value="not_published">Nieopublikowane na WP</SelectItem>
              <SelectItem value="failed">Błędy</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {selectedNodes.size > 0 && (
            <Button onClick={handleGenerateSelected} size="sm">
              <Play className="mr-1 h-3.5 w-3.5" />
              Generuj pełne ({selectedNodes.size})
            </Button>
          )}
          {stats.pending > 0 && (
            <Button onClick={handleBatchStructure} variant="outline" size="sm">
              <ListTree className="mr-1 h-3.5 w-3.5" />
              Etap 1: Struktury ({stats.pending})
            </Button>
          )}
          {stats.outlineReady > 0 && (
            <Button onClick={handleBatchContent} variant="default" size="sm">
              <Layers className="mr-1 h-3.5 w-3.5" />
              Etap 2: Artykuły ({stats.outlineReady})
            </Button>
          )}
          {/* "Pełny pipeline" przeniesiony do prominent banner na górze panelu */}
          {/* Sort buttony (Priorytet linków / Niewygenerowane / QA score) przeniesione do dedicated Filter box poniżej */}
          <Button onClick={handleClearCache} variant="ghost" size="sm" className="text-muted-foreground" title="Wyczyść cache SERP — wymuś ponowne crawlowanie">
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Wyczyść cache
          </Button>
          {isLogCollectorActive() && (
            <>
              <Button
                onClick={() => {
                  const allRuns = listLogRuns();
                  if (allRuns.length === 0) {
                    toast({ title: "Brak logów", description: "Uruchom najpierw generowanie w trybie debug." });
                    return;
                  }
                  // Download the most recent run
                  const latest = allRuns[0];
                  downloadLogRunAsTxt(latest.runId);
                  toast({ title: "Pobrano logi", description: `Run: ${latest.runId} (${latest.entryCount} wpisów)` });
                }}
                variant="outline"
                size="sm"
                className="text-violet-600 dark:text-violet-400 border-violet-500/30 bg-violet-500/5"
                title="Pobierz logi TXT z ostatniego runu (tylko w trybie debug)"
              >
                <Download className="mr-1 h-3.5 w-3.5" />
                Pobierz logi (debug)
              </Button>
              <Button
                onClick={() => {
                  const count = listLogRuns().length;
                  if (count === 0) {
                    toast({ title: "Brak logów do wyczyszczenia" });
                    return;
                  }
                  if (!confirm(`Usunąć ${count} runów logów z pamięci? (nie wpływa na wygenerowane artykuły)`)) return;
                  clearAllLogRuns();
                  toast({ title: "Logi wyczyszczone", description: `Usunięto ${count} runów z localStorage.` });
                }}
                variant="ghost"
                size="sm"
                className="text-violet-500/70 hover:text-violet-600"
                title="Usuń wszystkie logi debug z localStorage"
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                Wyczyść logi
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filter box — sort buttony grupowane wizualnie */}
      {(brokenLinksStats.nodesAffected > 0 || unfinishedCount > 0 || qaIssuesCount > 0) && (
        <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5 space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Filter className="h-3 w-3" />
            Filtruj po:
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {brokenLinksStats.nodesAffected > 0 && (
              <Button
                onClick={() => setTableSortBy((cur) => cur === "broken_links" ? "default" : "broken_links")}
                variant="outline"
                size="sm"
                className={
                  tableSortBy === "broken_links"
                    ? "border-violet-500/60 bg-violet-500/15 text-violet-700 dark:text-violet-300 hover:bg-violet-500/25"
                    : "border-violet-500/30 bg-violet-500/5 text-violet-700 dark:text-violet-400 hover:bg-violet-500/15"
                }
                title={
                  tableSortBy === "broken_links"
                    ? "Wyłącz sort priorytetu linków — wróć do domyślnej kolejności"
                    : `Sortuj nody wg liczby published artykułów linkujących do nich (SRO 11). ${brokenLinksStats.nodesAffected} nodów naprawi ${brokenLinksStats.totalLinks} broken links.`
                }
              >
                🔗 Priorytet linków ({brokenLinksStats.nodesAffected})
                {tableSortBy === "broken_links" && <span className="ml-1 text-[10px]">✓ aktywny</span>}
              </Button>
            )}
            {unfinishedCount > 0 && (
              <Button
                onClick={() => setTableSortBy((cur) => cur === "unfinished_first" ? "default" : "unfinished_first")}
                variant="outline"
                size="sm"
                className={
                  tableSortBy === "unfinished_first"
                    ? "border-amber-500/60 bg-amber-500/15 text-amber-700 dark:text-amber-300 hover:bg-amber-500/25"
                    : "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15"
                }
                title={
                  tableSortBy === "unfinished_first"
                    ? "Wyłącz sort niewygenerowanych — wróć do domyślnej kolejności"
                    : `Pokaż niewygenerowane na górze (${unfinishedCount} nodów: pending + failed + outline_ready). Batch poleci w tej kolejności.`
                }
              >
                📋 Niewygenerowane ({unfinishedCount})
                {tableSortBy === "unfinished_first" && <span className="ml-1 text-[10px]">✓ aktywny</span>}
              </Button>
            )}
            {qaIssuesCount > 0 && (
              <Button
                onClick={() => setTableSortBy((cur) => cur === "qa_score_asc" ? "default" : "qa_score_asc")}
                variant="outline"
                size="sm"
                className={
                  tableSortBy === "qa_score_asc"
                    ? "border-rose-500/60 bg-rose-500/15 text-rose-700 dark:text-rose-300 hover:bg-rose-500/25"
                    : "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400 hover:bg-rose-500/15"
                }
                title={
                  tableSortBy === "qa_score_asc"
                    ? "Wyłącz sort po QA — wróć do domyślnej kolejności"
                    : `Sortuj po QA score ASC — najgorsze QA na górze. ${qaIssuesCount} artykułów ma score < 100. Nieaudytowane na końcu.`
                }
              >
                ⚠ QA score ({qaIssuesCount})
                {tableSortBy === "qa_score_asc" && <span className="ml-1 text-[10px]">✓ aktywny</span>}
              </Button>
            )}
          </div>
          <p className="text-[11px] text-muted-foreground italic">
            💡 Aby sortować po klastrach (seedach) — kliknij kolumnę <strong className="text-foreground/80">Seed</strong> w tabeli poniżej.
          </p>
        </div>
      )}

      {/* Live Logs Panel — ABOVE the table */}
      {logs.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
            <button
              onClick={() => setShowLogs(!showLogs)}
              className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
            >
              <Terminal className="h-4 w-4" />
              Dziennik operacji ({logs.length})
              {generatingNodes.size > 0 && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              )}
            </button>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => {
                // Export logs as TXT with full debug data
                const txt = logs.map((log) => {
                  let line = `[${log.time}] [${log.type.toUpperCase()}] ${log.message}`;
                  if (log.debug) {
                    line += "\n" + JSON.stringify(log.debug, null, 2).split("\n").map((l) => "    " + l).join("\n");
                  }
                  return line;
                }).join("\n");
                const blob = new Blob([txt], { type: "text/plain" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `engine_log_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.txt`;
                a.click();
                URL.revokeObjectURL(url);
              }}>
                Pobierz TXT
              </Button>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setLogs([])}>
                Wyczyść
              </Button>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowLogs(!showLogs)}>
                {showLogs ? <X className="h-3.5 w-3.5" /> : <Terminal className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
          {showLogs && (
            <div ref={mainLogContainerRef} className="max-h-80 overflow-y-auto p-3 bg-background font-mono text-xs space-y-0.5">
              {logs.map((log, i) => (
                <div key={i}>
                  <div
                    className={`flex gap-2 ${log.debug ? "cursor-pointer hover:bg-muted/30 rounded px-1 -mx-1" : ""} ${
                      log.type === "error" ? "text-red-500 dark:text-red-400" :
                      log.type === "success" ? "text-green-600 dark:text-green-400" :
                      log.type === "stage" ? "text-blue-600 dark:text-blue-400" :
                      "text-muted-foreground"
                    }`}
                    onClick={() => log.debug && toggleLogExpand(i)}
                  >
                    <span className="text-muted-foreground/60 shrink-0">{log.time}</span>
                    <span className="flex-1">{log.message}</span>
                    {log.debug && (
                      <span className="text-muted-foreground/40 shrink-0">
                        {expandedLogs.has(i) ? "▾" : "▸"}
                      </span>
                    )}
                  </div>
                  {log.debug && expandedLogs.has(i) && (
                    <pre className="ml-16 mt-1 mb-2 p-2 rounded bg-muted/30 text-[10px] text-muted-foreground overflow-x-auto max-h-40 overflow-y-auto">
                      {JSON.stringify(log.debug, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
      )}

      {/* Info banner — sort aktywny wplywa na batch generowania */}
      {tableSortBy !== "default" && tableSortBy !== "broken_links" && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <span className="text-base">{tableSortBy === "seed_asc" ? "▲" : "▼"}</span>
            <span className="font-semibold">
              Sortowanie po seedach: {tableSortBy === "seed_asc" ? "A → Z" : "Z → A"}
            </span>
            <span className="text-muted-foreground font-normal">
              — batch JEDEN PRZYCISK poleci w tej kolejnosci
            </span>
          </div>
          <button
            type="button"
            onClick={() => setTableSortBy("default")}
            className="text-emerald-700 dark:text-emerald-400 hover:underline font-medium"
          >
            Wyczysc sort
          </button>
        </div>
      )}

      {/* Info card — sort broken_links aktywny — pokazuje konkretne metryki */}
      {tableSortBy === "broken_links" && (
        <div className="rounded-md border border-violet-500/40 bg-violet-500/5 px-4 py-3 text-xs space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
              <span className="text-lg">🔗</span>
              <span className="font-semibold text-sm">Priorytet linków — sortowanie aktywne</span>
            </div>
            <button
              type="button"
              onClick={() => setTableSortBy("default")}
              className="text-violet-700 dark:text-violet-400 hover:underline font-medium"
            >
              Wyłącz sort
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-1">
            <div className="rounded bg-violet-500/10 px-3 py-2">
              <div className="text-[10px] uppercase text-violet-600/80 dark:text-violet-400/80 font-semibold">Nody do wygenerowania</div>
              <div className="text-lg font-bold text-violet-800 dark:text-violet-200 tabular-nums">{brokenLinksStats.nodesAffected}</div>
              <div className="text-[10px] text-muted-foreground">unpublished z ≥1 broken linkiem do nich</div>
            </div>
            <div className="rounded bg-violet-500/10 px-3 py-2">
              <div className="text-[10px] uppercase text-violet-600/80 dark:text-violet-400/80 font-semibold">Total broken links</div>
              <div className="text-lg font-bold text-violet-800 dark:text-violet-200 tabular-nums">{brokenLinksStats.totalLinks}</div>
              <div className="text-[10px] text-muted-foreground">tyle 404 naprawisz publikując wszystkie</div>
            </div>
            <div className="rounded bg-violet-500/10 px-3 py-2">
              <div className="text-[10px] uppercase text-violet-600/80 dark:text-violet-400/80 font-semibold">Top score</div>
              <div className="text-lg font-bold text-violet-800 dark:text-violet-200 tabular-nums">{brokenLinksStats.topScore} →  0</div>
              <div className="text-[10px] text-muted-foreground">DESC — pierwsze nody mają największy impact</div>
            </div>
          </div>

          {brokenLinksStats.distribution.size > 0 && (
            <div className="pt-1 text-[11px] text-muted-foreground">
              <span className="font-medium text-violet-700 dark:text-violet-300">Rozkład: </span>
              {Array.from(brokenLinksStats.distribution.entries())
                .sort((a, b) => b[0] - a[0])
                .map(([score, count]) => `${count}× score=${score}`)
                .join(" · ")}
            </div>
          )}

          <div className="pt-1 text-[11px] text-muted-foreground italic">
            Klik <strong className="text-violet-700 dark:text-violet-300">"Generuj pełne"</strong> lub <strong className="text-violet-700 dark:text-violet-300">"Etap 1"</strong> poleci batch w tej kolejności — top scores first.
          </div>
        </div>
      )}

      {/* Nodes table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={selectedNodes.size === filteredNodes.length && filteredNodes.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>Tytuł artykułu</TableHead>
              <TableHead className="w-[150px]">
                <button
                  type="button"
                  onClick={cycleTableSort}
                  disabled={tableSortBy === "broken_links"}
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border transition-colors font-semibold uppercase text-[11px] ${
                    tableSortBy === "default"
                      ? "border-border bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground"
                      : tableSortBy === "broken_links"
                      ? "border-border bg-muted/20 text-muted-foreground/40 cursor-not-allowed"
                      : "border-emerald-500/60 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25"
                  }`}
                  title={
                    tableSortBy === "broken_links" ? "Sort po seedach wyłączony — aktywny sort priorytet linków (button na górze)" :
                    tableSortBy === "default" ? "Klik: sortuj seedami A→Z (batch poleci w tej kolejnosci)" :
                    tableSortBy === "seed_asc" ? "Klik: sortuj seedami Z→A (batch w tej kolejnosci)" :
                    "Klik: kolejnosc domyslna (DB order)"
                  }
                >
                  <span>Seed</span>
                  <span className="text-base leading-none">
                    {tableSortBy === "seed_asc" ? "▲" : tableSortBy === "seed_desc" ? "▼" : "⇅"}
                  </span>
                </button>
              </TableHead>
              <TableHead className="w-[130px]">Lifecycle</TableHead>
              <TableHead className="w-[80px]">QA</TableHead>
              <TableHead className="w-[180px]">Pipeline</TableHead>
              <TableHead className="w-[80px]">Akcja</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredNodes.map((node) => {
              const stage = getEngineStage(node.id);
              const content = contentMap.get(node.id);
              const isGenerating = generatingNodes.has(node.id);
              const seedName = seedNameMap.get(node.seed_id) || "";
              const lifecycle = isGenerating ? "in_generation" : computeLifecycle(content);
              const lifecycleInfo = LIFECYCLE_LABELS[lifecycle] || LIFECYCLE_LABELS.not_started;
              const isStale = (content?.is_stale ?? false) === true;
              const qualityScore = content?.quality_score ?? null;
              const qaStatus = (content?.qa_status ?? "pending");
              const publishCheck = canPublish(content);
              const isPillarNode = (node as any).document_role === "pillar" || (node as any).node_role === "pillar";

              // outline data — DB may store as array (sections) or object ({sections, distribution})
              const outlineRaw = content?.outline;
              const outlineSections = Array.isArray(outlineRaw) ? outlineRaw : outlineRaw?.sections;
              const hasOutline = (stage === "outline_ready" || stage === "ready" || stage === "published") && outlineSections && outlineSections.length > 0;
              const showOutline = hasOutline && !expandedOutlines.has(node.id);
              // content preview for ready/published nodes
              const hasFinalHtml = (stage === "ready" || stage === "published") && content?.final_html;
              const draftHtml = content?.final_html || content?.draft_html;
              const wordCount = draftHtml ? draftHtml.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length : 0;

              return (
                <React.Fragment key={node.id}>
                  <TableRow className="hover:bg-muted/30">
                    <TableCell>
                      <Checkbox
                        checked={selectedNodes.has(node.id)}
                        onCheckedChange={() => toggleSelect(node.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-2">
                        {isPillarNode && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tabular-nums bg-violet-500/20 text-violet-700 dark:text-violet-300 border border-violet-500/50 shrink-0 mt-0.5"
                            title="Hub klastra (pillar). Generuj w SRO → Pillar Page, nie tutaj. Publikacja na WP działa stąd normalnie."
                          >
                            <Layers className="h-3 w-3" />
                            PILLAR
                          </span>
                        )}
                        {(() => {
                          const score = brokenLinksScores.get(node.id) ?? 0;
                          if (score <= 0) return null;
                          return (
                            <span
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums bg-violet-500/15 text-violet-700 dark:text-violet-300 border border-violet-500/30 shrink-0 mt-0.5"
                              title={`${score} opublikowanych artykułów linkuje do tego nodu (broken right now). Wygenerowanie tego nodu naprawi ${score} broken links.`}
                            >
                              🔗 {score}
                            </span>
                          );
                        })()}
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{node.title}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[350px]">{node.canonical_question}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{seedName}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${lifecycleInfo.color}`}>
                          {isGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                          {lifecycleInfo.label}
                        </span>
                        {(content?.wp_post_id || content?.published_at) ? (
                          content?.wp_post_url ? (
                            <a
                              href={content.wp_post_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/30 underline-offset-2 hover:underline"
                              title={`Opublikowane na blogu WP${content?.published_at ? ` — ${new Date(content.published_at).toLocaleString("pl-PL")}` : ""}${content?.wp_post_id ? ` (post #${content.wp_post_id})` : ""}. Kliknij, aby otworzyć wpis.`}
                            >
                              🌐 Na WP{content?.published_at ? ` · ${new Date(content.published_at).toLocaleDateString("pl-PL")}` : ""}
                            </a>
                          ) : (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                              title={`Opublikowane na blogu WP${content?.published_at ? ` — ${new Date(content.published_at).toLocaleString("pl-PL")}` : ""}${content?.wp_post_id ? ` (post #${content.wp_post_id})` : ""}`}
                            >
                              🌐 Na WP{content?.published_at ? ` · ${new Date(content.published_at).toLocaleDateString("pl-PL")}` : ""}
                            </span>
                          )
                        ) : content?.final_html ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground" title="Artykuł gotowy, ale jeszcze nie opublikowany na blogu WP">
                            nie na WP
                          </span>
                        ) : null}
                        {isStale && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/15 text-red-700 dark:text-red-400">
                            Nieaktualne
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {qualityScore != null ? (
                        <span className={`text-xs font-medium ${qaStatus === "approved" ? "text-green-600 dark:text-green-400" : qaStatus === "failed" ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`}>
                          {qualityScore}/100
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <EngineProgress
                          currentStage={(stage as EngineStage) || "pending"}
                          updatedAt={content?.updated_at as string | undefined}
                        />
                        {(stage === "ready" || stage === "published") && (
                          <SchemaReadinessBadge nodeId={node.id} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {stage === "pending" && !isGenerating && (
                        isPillarNode ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerateStructure(node.id)}
                            className="h-7 text-xs bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/50 hover:bg-violet-500/20"
                            title="Pillary generujemy w panelu SRO Structure → Pillar Page (osobny pillarGenerator). Klik tutaj pokaże instrukcję."
                          >
                            <Layers className="h-3 w-3 mr-1" />
                            Generuj w SRO → Pillar Page
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleGenerateStructure(node.id)} className="h-7 text-xs">
                            <ListTree className="h-3 w-3 mr-1" />
                            Struktura
                          </Button>
                        )
                      )}
                      {stage === "skipped_existing" && !isGenerating && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetSkip(node.id, "regenerate")}
                            className="h-7 text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/20"
                            title="Audit wp_matching dopasował do istniejącego WP postu. Klik: wymuś regenerację (nowy artykuł, stary WP post zostaje)"
                          >
                            <Unlock className="h-3 w-3 mr-1" />
                            Odblokuj
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResetSkip(node.id, "skip_manual")}
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                            title="Zablokuj jako skip ręczny (świadoma decyzja — pipeline NIGDY nie regeneruje)"
                          >
                            <Lock className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {stage === "outline_ready" && !isGenerating && (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="default" onClick={() => handleGenerateContent(node.id)} className="h-7 text-xs">
                            <Layers className="h-3 w-3 mr-1" />
                            Treść
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteStructure(node.id)} className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500" title="Usuń strukturę">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {(stage === "failed" || (IN_GENERATION_STAGES.has(stage) && !isGenerating)) && (
                        <Button size="sm" variant="outline" onClick={() => handleDeleteStructure(node.id)} className="h-7 text-xs">
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Reset
                        </Button>
                      )}
                      {(stage === "ready" || stage === "published") && (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => setPreviewNodeId(node.id)} className="h-7 text-xs bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30">
                            <Eye className="h-3 w-3 mr-1" />
                            {wordCount > 0 ? `${wordCount} słów` : "Podgląd"}
                          </Button>
                          {useWorkerMode && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => handleGenerateContent(node.id)}
                              className="h-6 w-6 p-0 text-amber-600 hover:text-amber-700"
                              title="Regeneruj treść (zachowaj outline) — pokaże confirm z ostrzeżeniem"
                            >
                              <Zap className="h-3 w-3" />
                            </Button>
                          )}
                          {isLogCollectorActive() && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                // Try to find any run for this node (article, structure, or content)
                                const runs = listLogRuns();
                                const nodeRun = runs.find((r) =>
                                  r.runId === `article-${node.id}` ||
                                  r.runId === `content-${node.id}` ||
                                  r.runId === `structure-${node.id}`,
                                );
                                if (nodeRun) {
                                  downloadLogRunAsTxt(nodeRun.runId);
                                  toast({ title: "Pobrano", description: `${nodeRun.entryCount} wpisów` });
                                } else {
                                  toast({ title: "Brak logów dla tego node'a", variant: "destructive" });
                                }
                              }}
                              className="h-6 w-6 p-0 text-violet-500 hover:text-violet-600"
                              title="Pobierz logi TXT dla tego node'a (debug)"
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleClearContent(node.id)} className="h-6 w-6 p-0 text-muted-foreground hover:text-amber-500" title="Wyczyść treść (zachowaj strukturę H2/H3)">
                            <RotateCcw className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Outline preview row — auto-shown for outline_ready */}
                  {showOutline && (
                    <TableRow className="border-0">
                      <TableCell colSpan={7} className="p-0 pt-0 pb-2">
                        <div className="mx-4 mb-2 rounded-lg border border-violet-500/20 bg-violet-500/5 dark:bg-violet-500/10">
                          <div className="flex items-center justify-between px-4 py-2 border-b border-violet-500/10">
                            <div className="flex items-center gap-2">
                              <ListTree className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                              <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
                                Wygenerowana struktura artykułu
                              </span>
                              <span className="text-xs text-muted-foreground">
                                ({(outlineSections as Array<any>).filter((s: any) => s.type !== "intro").length} H2,{" "}
                                {(outlineSections as Array<any>).reduce((sum: number, s: any) => sum + (s.h3s?.length || 0), 0)} H3)
                              </span>
                            </div>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => toggleOutline(node.id)}
                              className="h-6 px-2 text-xs text-muted-foreground"
                            >
                              Zwiń
                            </Button>
                          </div>
                          <div className="px-4 py-3 space-y-1">
                            {(outlineSections as Array<any>).map((sec: any, si: number) => (
                              <div key={si}>
                                {sec.type === "intro" ? (
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground py-0.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                                    <span className="italic">Wstęp (intro)</span>
                                  </div>
                                ) : (
                                  <div className="py-0.5">
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-500/20 text-violet-700 dark:text-violet-300">
                                        H2
                                      </span>
                                      <span className="font-medium text-foreground">{sec.header || sec.source_area || sec.title || `Sekcja ${si + 1}`}</span>
                                      {sec.section_type && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                          {sec.section_type}
                                        </span>
                                      )}
                                    </div>
                                    {(sec.h3s || []).length > 0 && (
                                      <div className="ml-8 mt-0.5 space-y-0.5">
                                        {(sec.h3s || []).map((h3: any, hi: number) => (
                                          <div key={hi} className="flex items-center gap-2 text-xs">
                                            <span className="shrink-0 px-1 py-0.5 rounded text-[9px] font-semibold bg-blue-500/15 text-blue-600 dark:text-blue-400">
                                              H3
                                            </span>
                                            <span className="text-muted-foreground">{h3.header}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {filteredNodes.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>Brak node'ów pasujących do filtra.</p>
        </div>
      )}

      {/* Article Preview Dialog */}
      {previewNodeId && (() => {
        const previewNode = nodes.find((n) => n.id === previewNodeId);
        const previewContent = contentMap.get(previewNodeId);
        return (
          <ArticlePreviewDialog
            open={true}
            onOpenChange={(open) => { if (!open) setPreviewNodeId(null); }}
            nodeId={previewNodeId}
            nodeTitle={previewNode?.title || "Artykuł"}
            content={previewContent}
          />
        );
      })()}

      {/* Force regenerate confirm — pokazuje co user straci (slowa/koszt/czas) */}
      <ConfirmRegenerateDialog
        target={forceConfirm}
        onCancel={() => setForceConfirm(null)}
        onConfirm={handleForceConfirm}
      />

      {/* ============================================================ */}
      {/* FLOATING NOTIFICATION OVERLAY — JEDEN PRZYCISK batch progress  */}
      {/* Fixed bottom-right, persistent during batch, minimize/close   */}
      {/* ============================================================ */}
      {(batchRunning || (liveBatch.total > 0 && !floatingDismissed)) && !floatingDismissed && (
        <div
          className="fixed z-50 bottom-20 right-4 w-[min(440px,calc(100vw-2rem))] rounded-lg border shadow-2xl backdrop-blur-md"
          style={{ background: "rgba(15,23,42,0.96)", borderColor: "rgba(16,185,129,0.3)" }}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-zinc-800">
            <div className="flex items-center gap-2 min-w-0">
              <Zap className={`h-4 w-4 shrink-0 ${batchPaused ? "text-amber-400" : batchRunning ? "text-emerald-400 animate-pulse" : "text-emerald-500"}`} />
              <span className="text-xs font-medium text-zinc-100 truncate">
                JEDEN PRZYCISK
                {batchRunning && !batchPaused && " — w trakcie"}
                {batchRunning && batchPaused && " — PAUZA"}
                {!batchRunning && " — zakonczony"}
              </span>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-zinc-700 text-zinc-300">
                {liveBatch.current}/{liveBatch.total}
              </span>
              {liveBatch.failed > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-orange-500/40 text-orange-300 bg-orange-500/10" title="result.success=false (LLM zwrocil blad merytoryczny)">
                  ✗ {liveBatch.failed}
                </span>
              )}
              {liveBatch.skipped > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/10" title="Hard timeout 35min — node nie skonczyl w czasie">
                  ⏱ {liveBatch.skipped}
                </span>
              )}
              {liveBatch.errored > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-red-500/40 text-red-300 bg-red-500/10" title="Crash/exception/network (systemowe)">
                  💥 {liveBatch.errored}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setFloatingMinimized((v) => !v)}
                className="text-zinc-400 hover:text-zinc-100 transition p-1"
                title={floatingMinimized ? "Rozwin" : "Zwin"}
              >
                {floatingMinimized ? "▲" : "▼"}
              </button>
              {!batchRunning && (
                <button
                  type="button"
                  onClick={() => setFloatingDismissed(true)}
                  className="text-zinc-400 hover:text-zinc-100 transition px-1.5"
                  title="Zamknij"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {!floatingMinimized && (
            <div className="p-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-200">
                <span>Postep: <strong className="font-mono">{liveBatch.current}/{liveBatch.total}</strong></span>
                <span>ETA: <strong className="font-mono">~{liveBatch.etaMin}m</strong></span>
              </div>

              {/* Breakdown — pokazuje rozne typy outcome */}
              <div className="grid grid-cols-4 gap-1 text-[10px]">
                <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-1.5 text-center" title="Gotowe">
                  <div className="text-emerald-300 font-mono font-bold text-sm">{liveBatch.current - liveBatch.failed - liveBatch.skipped - liveBatch.errored}</div>
                  <div className="text-emerald-400/70 text-[9px] uppercase">✓ ok</div>
                </div>
                <div className="rounded border border-orange-500/30 bg-orange-500/5 p-1.5 text-center" title="Fail (result.success=false — LLM prompt-level)">
                  <div className="text-orange-300 font-mono font-bold text-sm">{liveBatch.failed}</div>
                  <div className="text-orange-400/70 text-[9px] uppercase">✗ fail</div>
                </div>
                <div className="rounded border border-amber-500/30 bg-amber-500/5 p-1.5 text-center" title="Timeout 35min — skip">
                  <div className="text-amber-300 font-mono font-bold text-sm">{liveBatch.skipped}</div>
                  <div className="text-amber-400/70 text-[9px] uppercase">⏱ timeout</div>
                </div>
                <div className="rounded border border-red-500/30 bg-red-500/5 p-1.5 text-center" title="Crash/exception/network (systemowe)">
                  <div className="text-red-300 font-mono font-bold text-sm">{liveBatch.errored}</div>
                  <div className="text-red-400/70 text-[9px] uppercase">💥 crash</div>
                </div>
              </div>

              <div className="h-1.5 bg-zinc-800 rounded overflow-hidden">
                <div
                  className={`h-full transition-all ${batchPaused ? "bg-amber-500" : batchRunning ? "bg-emerald-500" : "bg-emerald-600"}`}
                  style={{
                    width: liveBatch.total > 0 ? `${Math.min(100, (liveBatch.current / liveBatch.total) * 100)}%` : "0%",
                  }}
                />
              </div>

              {/* Heartbeat row — pokazuje że okno żyje + ostatnia aktywność */}
              {batchRunning && (
                <div className="flex items-center justify-between gap-2 text-[11px] text-zinc-300 border-b border-zinc-800 pb-2">
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${liveBatch.secsSinceLastActivity < 30 ? "bg-emerald-400 animate-pulse" : liveBatch.secsSinceLastActivity < 120 ? "bg-amber-400 animate-pulse" : "bg-red-500"}`} />
                    <span className="text-zinc-400">Concurrency:</span>
                    <strong className="text-zinc-100 font-mono">{liveBatch.concurrency}</strong>
                  </div>
                  <div>
                    <span className="text-zinc-400">Ostatnia aktywnosc:</span>{" "}
                    <strong className={`font-mono ${liveBatch.secsSinceLastActivity < 30 ? "text-emerald-300" : liveBatch.secsSinceLastActivity < 120 ? "text-amber-300" : "text-red-300"}`}>
                      {liveBatch.secsSinceLastActivity}s temu
                    </strong>
                  </div>
                </div>
              )}

              {/* Active nodes list — pokazuje wszystkie procesowane rownolegle */}
              {liveBatch.activeNodes.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[9px] uppercase tracking-wide text-zinc-500">
                    Aktywne node ({liveBatch.activeNodes.length})
                  </div>
                  {liveBatch.activeNodes.map((n) => {
                    const elapsedSec = Math.floor((Date.now() - n.startTs) / 1000);
                    const elapsedMm = Math.floor(elapsedSec / 60);
                    const elapsedSs = String(elapsedSec % 60).padStart(2, "0");
                    // 30:00 = hard cap. Kolor zmienia sie zbliz. do limitu:
                    // >20min = amber early warning, >27min = red zaraz timeout
                    const warnSoft = elapsedMm > 20;
                    const warnHard = elapsedMm > 27;
                    return (
                      <div key={n.id} className="rounded border border-emerald-500/20 bg-emerald-500/5 p-2 space-y-0.5">
                        <div className="flex items-center justify-between gap-2 text-[11px]">
                          <span className="text-zinc-100 font-medium truncate flex-1">{n.title}</span>
                          <span
                            className={`font-mono shrink-0 ${warnHard ? "text-red-300" : warnSoft ? "text-amber-300" : "text-zinc-300"}`}
                            title="Czas od startu node. Hard cap 30:00 — po tym czasie skip + lecimy dalej."
                          >
                            {elapsedMm}:{elapsedSs} / 30:00
                          </span>
                        </div>
                        <div className="text-[10px] text-emerald-300 font-mono truncate">
                          ↪ {n.stage || "start"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {batchRunning && (
                <div className="flex items-center gap-2">
                  {!batchPaused ? (
                    <Button size="sm" variant="outline" onClick={handleStopBatch} className="h-7 text-xs bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800">
                      ⏸ Stop
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={handleResumeBatch} className="h-7 text-xs bg-transparent border-amber-500/50 text-amber-300 hover:bg-amber-500/10">
                      ▶ Wznow
                    </Button>
                  )}
                </div>
              )}

              {logs.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[9px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
                    Log (ostatnie {Math.min(60, logs.length)} z {logs.length})
                  </div>
                  <div ref={floatingLogRef} className="max-h-44 overflow-auto rounded bg-black/60 p-2 font-mono text-[10px] leading-relaxed border border-zinc-800">
                    {logs.slice(-60).map((entry, idx) => {
                      const cls =
                        entry.type === "error" ? "text-red-400" :
                        entry.type === "success" ? "text-emerald-300" :
                        entry.type === "stage" ? "text-blue-300" :
                        "text-zinc-300";
                      return (
                        <div key={`${entry.time}-${idx}`} className={cls}>
                          <span className="text-zinc-500">[{entry.time}]</span> {entry.message}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reopen floating window button — when dismissed and batch results exist */}
      {floatingDismissed && liveBatch.total > 0 && !batchRunning && (
        <button
          type="button"
          onClick={() => { setFloatingDismissed(false); setFloatingMinimized(false); }}
          className="fixed z-40 bottom-20 right-4 px-3 py-2 rounded-lg border border-emerald-500/40 bg-zinc-900/90 backdrop-blur-md text-xs text-zinc-200 shadow-2xl hover:bg-zinc-800 transition flex items-center gap-2"
          title="Pokaz okno powiadomien"
        >
          <Zap className="h-3.5 w-3.5 text-emerald-400" />
          Pokaz okno batcha ({liveBatch.current}/{liveBatch.total})
        </button>
      )}

    </div>
  );
}
